import { httpClient } from './client';

export type MonetaryValue = {
  pricingCode?: string;
  value: number;
  code: string;
};

export type LimitTier = {
  endingQuantity: number;
  price: MonetaryValue;
};

export type SubscriptionLimit = {
  isUnlimited: boolean;
  limit: number;
  limitCode: string;
  tiers: LimitTier[] | null;
  isCustom: boolean;
};

export type SubscriptionLineItem = {
  isTrial: boolean;
  isIncludedInPlan: boolean;
  isAlphaUser: boolean;
  deactivationDateTimeUtc: string | null;
  trialEndDateTimeUtc: string | null;
  featureIds: string[];
  addOnCode: string | null;
};

export type UsageBillingDetails = {
  hasUsageBilling: boolean;
  perLabelFee: number | null;
  perMarkShippedFee: number | null;
};

export type CouponPlan = {
  code: string;
  name: string;
  billingPlanId: number | null;
  minEligibleVolume: number | null;
  basePlanPrice: MonetaryValue | null;
};

export type Coupon = {
  appliesForMonths: number | null;
  discountInCents: number | null;
  discountPercent: number | null;
  discountType: string;
  freeTrialAmount: number | null;
  freeTrialUnit: string;
  hostedDescription: string | null;
  invoiceDescription: string | null;
  isAppliedToInvoice: boolean | null;
  plans: CouponPlan[];
  createdAt: string;
  duration: string;
  temporalAmount: number | null;
  temporalUnit: string;
  lastApplicableBillingCycleDate: string | null;
};

export type BestActivePromotion = {
  coupon: Coupon;
  promotionType: string;
  minEligibleVolume: number | null;
};

export type SubscriptionBillingAddOn = {
  code: string;
  quantity: number;
  cost: MonetaryValue;
};

export type ManageSubscriptionPlan = {
  billingPlanId: string;
  name: string;
  code: string;
  isCurrentPlanInSellerPlanProfile: boolean;
  pricePerPeriod: MonetaryValue;
  pricePerMonth: MonetaryValue;
  userAddOnPrice: MonetaryValue;
  description: string;
  userLimit: SubscriptionLimit;
  shipmentLimit: SubscriptionLimit;
  hasChatSupport: boolean;
  hasPhoneSupport: boolean;
  supportOptions: string[];
  hasBranding: boolean;
  hasSecurity: boolean;
  isSelfManaged: boolean;
  stampsPlanCode: string | null;
  squarePlanCode: string | null;
  apiCode: string | null;
  hasShipEngineAccess: boolean;
  currencyType: string;
  subscriptionLineItems: SubscriptionLineItem[];
  usageBillingDetails: UsageBillingDetails;
  isOnAnnualBilling: boolean;
  bestActivePromotion: BestActivePromotion | null;
  analyticsLicenseLimit: SubscriptionLimit;
  additionalAnalyticsLicenses: number;
  analyticsLicenseAddOnPrice: MonetaryValue;
};

export type SubscriptionPlan = {
  planCode: string;
  name: string;
  pricePerPeriod: MonetaryValue;
  monthlyFee: MonetaryValue;
  isOnAnnualBilling: boolean;
};

/**
 * Most fields are null for sellers without an active paid subscription (e.g. in
 * trial), where only `billingProvider`, the boolean flags, and `usageBillingDetails`
 * are populated. Plan/pricing details for these sellers come from `currentBillingPlan`.
 */
export type ManageSubscriptionCurrentSubscription = {
  subscriptionId: string | null;
  billingProvider: number;
  account: Record<string, unknown> | null;
  subscriptionPlan: SubscriptionPlan | null;
  subscriptionStartDateTimeUTC: string | null;
  currentPeriodStartDateTimeUTC: string | null;
  currentPeriodEndDateTimeUTC: string | null;
  trialPeriodEndDateTimeUTC: string | null;
  canceledDateTimeUTC: string | null;
  expireDateTimeUTC: string | null;
  status: string | null;
  isCurrent: boolean;
  inTrial: boolean;
  isPendingSubscription: boolean;
  pendingPlanCode: string | null;
  pendingPlanName: string | null;
  pendingStartDateUTC: string | null;
  pricePerPeriod: MonetaryValue | null;
  monthlyFee: MonetaryValue | null;
  billingAddOns: SubscriptionBillingAddOn[] | null;
  pendingBillingAddOns: SubscriptionBillingAddOn[] | null;
  usageBillingDetails: UsageBillingDetails;
  shipmentVolumeTiers: LimitTier[] | null;
  couponCode: string | null;
  pricePerAdditionalUser: MonetaryValue | null;
};

export type ManageSubscriptionSuggestedPlan = {
  planID: string;
  planName: string;
  planPrice: MonetaryValue;
  planCode: string;
  orders: number;
  markets: number;
  users: number;
  hasSecurity: boolean;
};

export type PromotionUpgradeDetails = {
  targetBillingPlan: ManageSubscriptionPlan;
  targetCoupon: Coupon;
  isAccepted: boolean;
  promotionType: string;
};

export type ManageSubscriptionResponse = {
  currentBillingPlan: ManageSubscriptionPlan | null;
  currentSubscription: ManageSubscriptionCurrentSubscription;
  planProfileCode: string;
  sellerDefaultPlanProfileCode: string;
  allPlans: ManageSubscriptionPlan[];
  defaultProfilePlans: ManageSubscriptionPlan[];
  suggestedPlan: ManageSubscriptionSuggestedPlan | null;
  billingProviderId: string;
  coupons: Coupon[];
  planToNonWalletAccessPlanMap: Record<string, ManageSubscriptionPlan>;
  promotionUpgradeDetails: PromotionUpgradeDetails | null;
  recommendedBillingPlanIds: string[];
};

/** Fetch current billing plan, subscription, and all available plans. */
export const getManageSubscription = async (): Promise<ManageSubscriptionResponse> => {
  const response = await httpClient.get<ManageSubscriptionResponse>('/account/managesubscription');
  return response.data;
};
